import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import AIRecommendationCard from "@/components/AIRecommendationCard";
import RiskUsagePanel from "@/components/RiskUsagePanel";
import Watchlist from "@/components/Watchlist";
import { fmtMoney, fmtPct, pctClass } from "@/lib/format";
import { Brain, ArrowsClockwise, Plug } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [conns, setConns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadAll = async () => {
    try {
      const [s, sum, c] = await Promise.all([
        api.get("/signals", { params: { status_filter: "pending" } }),
        api.get("/portfolio/summary"),
        api.get("/brokers/connections"),
      ]);
      setSignals(s.data);
      setSummary(sum.data);
      setConns(c.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadAll(); }, []);

  // Refresh portfolio summary every 8s
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await api.get("/portfolio/summary");
        setSummary(r.data);
      } catch (e) { /* ignore */ }
    }, 8000);
    return () => clearInterval(id);
  }, []);

  const handleGenerate = async () => {
    if (conns.length === 0) {
      toast.error("Connect at least one broker first.");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/signals/generate");
      toast.success(`${r.data.length} signals generated`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate signals.");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (sig) => {
    setBusy(true);
    try {
      const r = await api.post(`/signals/${sig.id}/approve`);
      if (r.data.filled) toast.success(`Approved & filled ${sig.symbol}`);
      else toast.error(`${sig.symbol} blocked by risk engine.`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (sig) => {
    setBusy(true);
    try {
      await api.post(`/signals/${sig.id}/reject`);
      toast.success(`${sig.symbol} rejected`);
      loadAll();
    } catch (e) {
      toast.error("Failed to reject");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="grid grid-cols-12 gap-px" style={{ background: "var(--border)" }}>
        {/* Left rail: Brokers + Watchlist */}
        <aside className="col-span-12 lg:col-span-3 bg-[#0A0D14] p-4 space-y-4 min-h-[calc(100vh-56px)]">
          <BrokersStrip conns={conns} />
          <Watchlist />
        </aside>

        {/* Center: Signals + Portfolio summary */}
        <section className="col-span-12 lg:col-span-6 bg-[#0A0D14] p-4 space-y-4">
          <PortfolioSummaryCard summary={summary} />

          <div className="panel">
            <div className="panel-header">
              <span className="flex items-center gap-2"><Brain size={12} weight="bold"/> AI Trade Recommendations</span>
              <button
                className="btn-outline"
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-signals-btn"
              >
                <ArrowsClockwise size={11} className={generating ? "animate-spin" : ""} />
                <span className="ml-1">{generating ? "Analyzing markets…" : "Generate signals"}</span>
              </button>
            </div>
            <div className="p-4">
              {signals.length === 0 ? (
                <EmptySignals onGenerate={handleGenerate} hasBroker={conns.length > 0} />
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="signals-grid">
                  {signals.map((s) => (
                    <AIRecommendationCard
                      key={s.id}
                      signal={s}
                      busy={busy}
                      onApprove={handleApprove}
                      onReject={handleReject}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Right rail: Risk + Mode */}
        <aside className="col-span-12 lg:col-span-3 bg-[#0A0D14] p-4 space-y-4 min-h-[calc(100vh-56px)]">
          <RiskUsagePanel />
          <ExposurePanel summary={summary} />
        </aside>
      </div>
    </Layout>
  );
}

function PortfolioSummaryCard({ summary }) {
  if (!summary) {
    return <div className="panel p-4 text-xs text-[#4B5563]">Loading portfolio…</div>;
  }
  const pnl = summary.total_unrealized_pnl;
  return (
    <div className="panel" data-testid="portfolio-summary-card">
      <div className="panel-header">
        <span>Unified Portfolio</span>
        <Link to="/portfolio" className="text-[10px] text-[#8B95A5] hover:text-[#00E5FF]">View positions →</Link>
      </div>
      <div className="grid grid-cols-4 gap-px bg-[#272A30]">
        <Cell label="Market Value" value={fmtMoney(summary.total_market_value)} mono />
        <Cell label="Unrealized P&L" value={fmtMoney(pnl)} valueClass={pctClass(pnl)} mono testId="dash-pnl"/>
        <Cell label="Return %" value={fmtPct(summary.total_unrealized_pnl_pct)} valueClass={pctClass(summary.total_unrealized_pnl_pct)} mono />
        <Cell label="Open Positions" value={summary.open_positions} mono />
      </div>
    </div>
  );
}

function Cell({ label, value, valueClass = "text-white", mono, testId }) {
  return (
    <div className="bg-[#11141A] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B95A5]">{label}</div>
      <div className={`mt-2 text-xl ${mono ? "font-mono" : ""} ${valueClass}`}>{value}</div>
    </div>
  );
}

function ExposurePanel({ summary }) {
  if (!summary) return null;
  const stockMV = summary.exposure_by_asset?.stock || 0;
  const cryptoMV = summary.exposure_by_asset?.crypto || 0;
  const total = stockMV + cryptoMV || 1;
  const stockPct = (stockMV / total) * 100;
  const cryptoPct = (cryptoMV / total) * 100;

  return (
    <div className="panel" data-testid="exposure-panel">
      <div className="panel-header">
        <span>Exposure</span>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] mb-1">
            <span className="text-[#8B95A5]">Equities</span>
            <span className="font-mono text-white">{fmtMoney(stockMV)}</span>
          </div>
          <div className="risk-bar"><span style={{ width: `${stockPct}%`, background: "var(--ai)" }} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] mb-1">
            <span className="text-[#8B95A5]">Crypto</span>
            <span className="font-mono text-white">{fmtMoney(cryptoMV)}</span>
          </div>
          <div className="risk-bar"><span style={{ width: `${cryptoPct}%`, background: "#FFB300" }} /></div>
        </div>
        <div className="text-[10px] text-[#4B5563] uppercase tracking-[0.2em] pt-2 border-t border-[#272A30]">By Broker</div>
        <div className="space-y-1">
          {Object.entries(summary.exposure_by_broker || {}).length === 0 ? (
            <div className="text-[10px] text-[#4B5563]">No exposure yet.</div>
          ) : Object.entries(summary.exposure_by_broker).map(([b, v]) => (
            <div key={b} className="flex items-center justify-between text-xs">
              <span className="capitalize text-[#8B95A5]">{b}</span>
              <span className="font-mono">{fmtMoney(v)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrokersStrip({ conns }) {
  return (
    <div className="panel" data-testid="brokers-strip">
      <div className="panel-header">
        <span>Connected</span>
        <Link to="/brokers" className="text-[10px] flex items-center gap-1 text-[#8B95A5] hover:text-[#00E5FF]" data-testid="brokers-strip-manage">
          <Plug size={11} weight="bold" /> Manage
        </Link>
      </div>
      <div className="p-3 space-y-2">
        {conns.length === 0 ? (
          <div className="text-xs text-[#4B5563]">No brokers yet. <Link to="/brokers" className="text-[#00E5FF]">Connect one</Link> to begin.</div>
        ) : conns.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-2 border border-[#272A30] rounded-sm" data-testid={`broker-strip-${c.broker_id}`}>
            <div>
              <div className="text-xs">{c.display_name}</div>
              <div className="text-[10px] text-[#8B95A5] uppercase tracking-[0.18em]">{c.market} · {c.region}</div>
            </div>
            <span className={`tag ${c.status === "connected" ? "buy" : "hold"}`}>{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptySignals({ onGenerate, hasBroker }) {
  return (
    <div className="text-center py-10" data-testid="signals-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 border border-[#272A30] rounded-sm mb-3">
        <Brain size={20} weight="bold" className="text-[#00E5FF]" />
      </div>
      <div className="text-sm">No active recommendations.</div>
      <div className="text-xs text-[#8B95A5] mt-1 mb-4">
        {hasBroker
          ? "Run the AI scanner to surface trade ideas across your connected markets."
          : "Connect a broker first to enable signal generation."}
      </div>
      <button onClick={onGenerate} className="btn-primary" disabled={!hasBroker} data-testid="signals-empty-generate">
        Generate signals
      </button>
    </div>
  );
}
